import { formatAud, PRODUCT_CONFIG } from "@stb/product-config"
import { createFileRoute } from "@tanstack/react-router"
import { Check, CreditCard, FileText, Hourglass, Route as RouteIcon, Sparkles } from "lucide-react"
import type { LucideIcon } from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import { gateCta } from "@/core/launch"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import { Breadcrumb, CtaBand, FaqAccordion, SimpleTable } from "../components/landing"
import { HeroBand, StatRow } from "../components/landing/page-parts"
import { IconTile } from "../components/landing/product-art"

const REVIEWED = "28 October 2026"
const TRIAL_HREF = "/login"
const planCta = (label: string) => gateCta({ href: TRIAL_HREF, label }, "en")

// GST wording removed until accountant confirms (T643): no tax statement about prices on this page (Owner 2026-10-30).
export const pricingSeo = () =>
  buildHead({
    title: "Pricing",
    description: `Free, Logbook and Pro plans for sole traders. New accounts get a ${TRIAL_DAYS}-day free trial of Logbook or Pro, no card needed. Monthly or yearly billing.`,
    path: "/pricing",
    jsonLd: [breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Pricing" }])],
  })

export const Route = createFileRoute("/_brand-layout/pricing")({
  head: () => {
    const { meta, links, scripts } = pricingSeo()
    return { meta, links, scripts }
  },
  component: PricingPage,
})

const { free, logbook, pro } = PRODUCT_CONFIG.plans
const perMonth = (n: number | null, unit: string) =>
  n === null ? `Unlimited ${unit}` : `${n} ${unit} a month`
const cell = (n: number | null) => (n === null ? "Unlimited" : `${n} a month`)

interface Plan {
  name: string
  icon: LucideIcon
  tone: "primary" | "success"
  price: string
  per?: string
  yearly?: string
  note?: string
  badge?: string
  features: string[]
  cta: string
  primary?: boolean
}

const PLANS: Plan[] = [
  {
    name: "Free",
    icon: Sparkles,
    tone: "primary",
    price: formatAud(free.monthlyCents),
    note: "After your trial, if you do not subscribe",
    features: [
      `${free.tripsMonthly} GPS trips a month`,
      `${free.invoicesMonthly} invoices a month`,
      `${free.aiReadsMonthly} AI receipt reads a month`,
      `${free.vehicles} vehicles`,
    ],
    cta: "Start free",
  },
  {
    name: "Logbook",
    icon: RouteIcon,
    tone: "success",
    price: formatAud(logbook.monthlyCents),
    per: "/ month",
    yearly: `or ${formatAud(logbook.yearlyCents)} a year`,
    features: [
      perMonth(logbook.tripsMonthly, "GPS trips"),
      "Cents per km and logbook methods",
      "Logbook report and CSV export",
      `${logbook.vehicles} vehicles, extra ${formatAud(PRODUCT_CONFIG.addons.vehicle.monthlyCents)} each / month`,
    ],
    cta: "Choose Logbook",
  },
  {
    name: "Pro",
    icon: FileText,
    tone: "primary",
    price: formatAud(pro.monthlyCents),
    per: "/ month",
    yearly: `or ${formatAud(pro.yearlyCents)} a year`,
    badge: "Includes invoicing",
    features: [
      "Everything in Logbook",
      "Unlimited tax invoices",
      `${pro.aiReadsMonthly} AI receipt reads a month`,
      "GST on each invoice",
      `${pro.vehicles} vehicles, extra ${formatAud(PRODUCT_CONFIG.addons.vehicle.monthlyCents)} each / month`,
    ],
    cta: "Choose Pro",
    primary: true,
  },
]

const STEPS = [
  ["Tell us what you need", "Logbook if you only track your car, otherwise Pro. No card needed."],
  [`Use it for ${TRIAL_DAYS} days`, `Everything in the plan you chose, for ${TRIAL_DAYS} days.`],
  [
    "Subscribe or stay free",
    "You move to the Free plan unless you subscribe. Your records stay available to view and export.",
  ],
] as const

const BILLING_FAQ = [
  {
    question: "What happens when the trial ends?",
    answer:
      "You move to the Free plan unless you subscribe. Your records stay available to view and export.",
  },
  {
    question: "Can I cancel any time?",
    answer: "Yes. You buy and manage your plan on our website, so you cancel there.",
  },
]

export function PricingPage() {
  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Pricing" }]} />
      <HeroBand>
        {PRODUCT_CONFIG.pricesDraft && (
          <p className="bg-accent text-accent-foreground inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold">
            <Hourglass className="size-3.5" aria-hidden="true" />
            Draft prices. Not final.
          </p>
        )}
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">
          Pick the plan that fits your work
        </h1>
        <p className="text-muted-foreground max-w-[60ch] text-lg">
          New accounts get a {TRIAL_DAYS}-day free trial of Logbook or Pro, depending on what you
          choose at sign-up. No card needed.
        </p>
        <StatRow
          label="Key facts"
          stats={[
            { value: `${TRIAL_DAYS} days`, label: "free trial", icon: Sparkles, tone: "primary" },
            { value: "No card", label: "to start", icon: CreditCard, tone: "success" },
          ]}
        />
      </HeroBand>

      <section aria-label="Plans" role="region" className="px-band py-8">
        <div className="grid gap-4 md:grid-cols-3">
          {PLANS.map((plan) => (
            <article
              key={plan.name}
              className={`bg-card grid content-start gap-3 rounded-xl border p-6 ${plan.primary ? "border-primary border-2" : "border-border"}`}
            >
              <div className="flex flex-wrap items-center gap-3">
                <IconTile icon={plan.icon} tone={plan.tone} />
                <h2 className="text-xl font-semibold">{plan.name}</h2>
                {plan.badge && (
                  <span className="bg-primary/10 text-primary rounded-full px-3 py-0.5 text-xs font-semibold">
                    {plan.badge}
                  </span>
                )}
              </div>
              {plan.note && <p className="text-muted-foreground text-sm">{plan.note}</p>}
              <p>
                <span className="text-4xl font-bold">{plan.price}</span>
                {plan.per && <span className="text-muted-foreground"> {plan.per}</span>}
              </p>
              {plan.yearly && <p className="text-muted-foreground text-sm">{plan.yearly}</p>}
              <ul className="grid gap-2 text-sm">
                {plan.features.map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="text-primary mt-0.5 size-4 shrink-0" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href={planCta(plan.cta).href}
                className={`focus-visible:ring-ring mt-2 inline-flex min-h-11 items-center justify-center rounded-md px-4 font-medium outline-none focus-visible:ring-2 ${plan.primary ? "bg-primary text-primary-foreground" : "border-border border"}`}
              >
                {planCta(plan.cta).label}
              </a>
            </article>
          ))}
        </div>
        <p className="text-muted-foreground mt-4 text-sm">
          You buy a plan on our website. The iPhone and Android apps do not take payments.
        </p>
        <aside
          role="note"
          aria-label="New customer offer"
          className="border-border bg-card mt-4 rounded-lg border border-dashed p-4 text-sm"
        >
          <b>New customer offer</b>
          <p className="text-muted-foreground mt-1">
            A limited-time test for new customers on Pro monthly. It starts after your free trial
            and does not apply to Logbook or yearly plans. List prices stay as shown. Open to new
            customers who start paying on or before 31 March 2027 (Australian Eastern time).
            Customers who already have the offer keep it for their 6 months.
          </p>
        </aside>
      </section>

      <section aria-labelledby="team-plan-title" className="px-band py-8">
        <div className="border-border bg-card grid gap-4 rounded-xl border p-6 md:grid-cols-[1fr_1.4fr] md:items-start">
          <div className="grid content-start gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="team-plan-title" className="text-xl font-semibold">
                Team plan
              </h2>
              <span className="bg-muted text-muted-foreground rounded-full px-3 py-0.5 text-xs font-semibold">
                Coming soon
              </span>
            </div>
            <p>
              <span className="text-4xl font-bold">
                {formatAud(PRODUCT_CONFIG.team.baseMonthlyCents)}
              </span>
              <span className="text-muted-foreground"> / month</span>
            </p>
            <p className="text-muted-foreground text-sm">
              or {formatAud(PRODUCT_CONFIG.team.baseYearlyCents)} a year
            </p>
          </div>
          <ul className="grid gap-2 text-sm">
            {[
              `Includes ${PRODUCT_CONFIG.team.includedSeats} member seats as well as the account owner`,
              `Extra seats ${formatAud(PRODUCT_CONFIG.team.extraSeatMonthlyCents)} a month or ${formatAud(PRODUCT_CONFIG.team.extraSeatYearlyCents)} a year each`,
            ].map((f) => (
              <li key={f} className="flex gap-2">
                <Check className="text-primary mt-0.5 size-4 shrink-0" aria-hidden="true" />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-muted/40 border-border px-band border-y py-8">
        <h2 className="text-2xl font-semibold">How the free trial works</h2>
        <ol className="mt-4 grid gap-4 md:grid-cols-3">
          {STEPS.map(([title, body], i) => (
            <li key={title} className="bg-card border-border flex gap-3 rounded-lg border p-4">
              <span
                className="bg-primary text-primary-foreground grid size-8 shrink-0 place-items-center rounded-full font-bold"
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="text-muted-foreground text-sm">{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="px-band py-8">
        <h2 className="mb-4 text-2xl font-semibold">Compare plans</h2>
        <SimpleTable
          caption="Compare plans"
          columns={["What you get", "Free", "Logbook", "Pro"]}
          rows={[
            {
              header: "GPS trips",
              cells: [cell(free.tripsMonthly), cell(logbook.tripsMonthly), cell(pro.tripsMonthly)],
            },
            {
              header: "Tax invoices",
              cells: [cell(free.invoicesMonthly), "Not in Logbook", cell(pro.invoicesMonthly)],
            },
            {
              header: "AI receipt reads",
              cells: [cell(free.aiReadsMonthly), "Not in Logbook", cell(pro.aiReadsMonthly)],
            },
            {
              header: "Vehicles",
              cells: [
                String(free.vehicles),
                `${logbook.vehicles} (+${formatAud(PRODUCT_CONFIG.addons.vehicle.monthlyCents)})`,
                `${pro.vehicles} (+${formatAud(PRODUCT_CONFIG.addons.vehicle.monthlyCents)})`,
              ],
            },
            { header: "Logbook report, CSV", cells: ["Within limit", "Yes", "Yes"] },
            { header: "GST on each invoice", cells: ["—", "—", "Yes"] },
          ]}
        />
        <p className="text-muted-foreground mt-3 text-sm">
          Last reviewed {REVIEWED}. Logbook is a mileage-only plan. Upgrade to Pro for invoices and
          receipts.
        </p>
      </section>

      <section className="px-band py-8">
        <h2 className="mb-4 text-2xl font-semibold">Billing questions</h2>
        <FaqAccordion items={BILLING_FAQ} />
        <p>
          More answers are on the{" "}
          <a
            href="/faq"
            className="focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
          >
            frequently asked questions
          </a>{" "}
          page.
        </p>
      </section>

      <CtaBand
        title={`Start your ${TRIAL_DAYS}-day free trial`}
        description="No card needed."
        ctaLabel="Start free trial"
        ctaHref={TRIAL_HREF}
      />
    </>
  )
}
